import React, { useState, useMemo } from 'react';
import {
    Clock,
    CheckCircle2,
    XCircle,
    DollarSign,
    Calendar,
    RefreshCw,
    Search,
    Filter,
    Download,
    BarChart3
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { usePayments } from '../../contexts/PaymentContext';
import { getStaffDisplayName } from '../../utils/staffDisplay';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PaymentManagement = () => {
    const { user, allUsersMap } = useAuth();
    const { payments, updateRequestStatus, loading, deleteAllMonthlyPayments, refreshPayments } = usePayments();
    const [filterStatus, setFilterStatus] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Month selection state (default to current month YYYY-MM)
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    // Generate current and PAST 12 months for selector (Historical looking)
    const availableMonths = useMemo(() => {
        const months = [
            { value: 'all', label: 'Todos os Períodos' } // Add option specifically for "All Time"
        ];
        const today = new Date();

        // Include current month + past 13 months
        for (let i = 0; i < 14; i++) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            months.push({ value, label });
        }
        return months;
    }, []);

    // 1. First, filter by selected month OR show all
    const paymentsInMonth = useMemo(() => {
        if (selectedMonth === 'all') return payments;

        return payments.filter(p => {
            const date = new Date(p.createdAt);
            const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            return monthStr === selectedMonth;
        });
    }, [payments, selectedMonth]);

    // 2. Calculate stats based on the MONTHLY data
    const monthStats = useMemo(() => {
        const pending = paymentsInMonth.filter(p => p.status === 'pending');
        const approved = paymentsInMonth.filter(p => p.status === 'approved');
        const rejected = paymentsInMonth.filter(p => p.status === 'rejected');

        const awaitingVal = pending.reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);
        const approvedVal = approved.reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);

        return {
            pendingCount: pending.length,
            approvedCount: approved.length,
            rejectedCount: rejected.length,
            awaitingPaymentValue: awaitingVal,
            approvedValue: approvedVal
        };
    }, [paymentsInMonth]);

    // 3. Filter for display list (Status + Search)
    const filteredPayments = useMemo(() => {
        return paymentsInMonth.filter(p => {
            const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
            const matchesSearch = p.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.userId?.includes(searchTerm);
            return matchesStatus && matchesSearch;
        });
    }, [paymentsInMonth, filterStatus, searchTerm]);

    // 4. Calculate Trend Data for Chart
    const trendData = useMemo(() => {
        const groups = {};
        // Use all payments for trend to give context
        const sourceData = payments;

        sourceData.forEach(p => {
            const date = new Date(p.createdAt);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });

            if (!groups[monthKey]) {
                groups[monthKey] = {
                    name: monthLabel,
                    sortKey: monthKey,
                    Solicitado: 0,
                    Aprovado: 0
                };
            }

            const val = parseFloat(p.totalValue) || 0;
            groups[monthKey].Solicitado += val;
            if (p.status === 'approved') {
                groups[monthKey].Aprovado += val;
            }
        });

        // Convert to array and sort (last 6 months)
        return Object.values(groups)
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
            .slice(-6);
    }, [payments]);

    const handleExportCSV = () => {
        if (!filteredPayments.length) {
            alert("Não há dados para exportar.");
            return;
        }

        const headers = ["Data", "ID", "Nome", "Cargo", "Status", "Valor Padrao", "Valor Extra", "Total", "Chave PIX"];

        const rows = filteredPayments.map(p => {
            const requestUser = allUsersMap ? allUsersMap[p.userId] : null;
            const codename = requestUser ? getStaffDisplayName(requestUser) : (p.userName || '-');
            const role = getReadableRole(requestUser?.staffDetails?.role || requestUser?.accessLevel || 'User');
            const pix = requestUser?.staffDetails?.bankingData?.pixKey || '-';

            return [
                new Date(p.createdAt).toLocaleDateString('pt-BR'),
                p.userId,
                `"${codename}"`,
                `"${role}"`,
                p.status,
                (p.standardValue || 0).toString().replace('.', ','),
                (p.extraValue || 0).toString().replace('.', ','),
                (p.totalValue || 0).toString().replace('.', ','),
                pix
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(";") + "\n"
            + rows.map(e => e.join(";")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `pagamentos_${selectedMonth === 'all' ? 'todos' : selectedMonth}_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const handleApprove = (id) => {
        if (window.confirm('Confirmar aprovação do pagamento?')) {
            updateRequestStatus(id, 'approved', user.discordId);
        }
    };

    const handleReject = (id) => {
        openRejectionModal(id);
    };

    const handleCopyPix = (pixKey) => {
        navigator.clipboard.writeText(pixKey);
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refreshPayments();
        } catch (error) {
            console.error("Refresh failed", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const styles = {
            pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
            approved: 'bg-green-500/10 text-green-500 border-green-500/20',
            rejected: 'bg-red-500/10 text-red-500 border-red-500/20'
        };

        const labels = {
            pending: 'Em Análise',
            approved: 'Aprovado',
            rejected: 'Rejeitado'
        };

        return (
            <Badge className={`${styles[status] || styles.pending} border`}>
                {labels[status] || status}
            </Badge>
        );
    };

    // Role Mapping Helper
    const getReadableRole = (roleKey) => {
        const roles = {
            'suporte': 'Suporte',
            'suporte_compras': 'Suporte Compras',
            'suporte_descontos': 'Suporte Descontos',
            'moderador': 'Moderador',
            'super_moderador': 'Super Moderador',
            'programador': 'Programador',
            'head_suporte': 'Head Suporte',
            'head_moderacao': 'Head Moderação',
            'head_staff': 'Head Staff',
            'analista_qualidade': 'Analista de Qualidade',
            'departamento_juridico': 'Jurídico',
            'criador_de_conteudo': 'Criador de Conteúdo',
            'admin': 'Administrador'
        };
        return roles[roleKey] || roleKey?.replace(/_/g, ' ') || 'User';
    };

    // Rejection Modal State
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    const openRejectionModal = (id) => {
        setSelectedRequestId(id);
        setRejectionReason('');
        setRejectionModalOpen(true);
    };

    const confirmRejection = async () => {
        if (!rejectionReason.trim()) {
            alert('Por favor, informe o motivo da rejeição.');
            return;
        }
        await updateRequestStatus(selectedRequestId, 'rejected', user.discordId, rejectionReason);
        setRejectionModalOpen(false);
    };

    if (loading) return <div>Carregando...</div>;

    const handleDeleteAll = async () => {
        const monthLabel = availableMonths.find(m => m.value === selectedMonth)?.label;
        if (window.confirm(`ATENÇÃO: Você tem certeza que deseja excluir TODAS as solicitações de pagamento de ${monthLabel}?`)) {
            if (window.confirm(`Esta ação é irreversível e apagará ${paymentsInMonth.length} registros (Pendentes, Aprovados e Rejeitados).\n\nDeseja realmente continuar?`)) {

                const result = await deleteAllMonthlyPayments(selectedMonth);
                if (result.success) {
                    alert('Solicitações excluídas com sucesso!');
                } else {
                    alert('Erro ao excluir solicitações. Consulte o console.');
                }
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                        Solicitação de Pagamentos
                    </h1>
                    <p className="text-gray-400 mt-2">Gerencie as solicitações de pagamento</p>
                </div>

                <div className="w-full md:w-auto flex flex-col md:flex-row gap-3">
                    <div className="w-full md:w-64">
                        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                            <SelectTrigger className="w-full bg-gray-900 border-gray-700 text-white">
                                <SelectValue placeholder="Selecione o mês" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-900 border-gray-700">
                                {availableMonths.map(month => (
                                    <SelectItem key={month.value} value={month.value} className="text-white hover:bg-gray-800 focus:bg-gray-800">
                                        {month.label.charAt(0).toUpperCase() + month.label.slice(1)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="requests" className="w-full space-y-8">
                <div className="flex justify-center">
                    <TabsList className="bg-black/40 border border-gray-800 p-1 rounded-full gap-2 h-auto inline-flex shadow-xl shadow-black/50 backdrop-blur-md">
                        <TabsTrigger
                            value="requests"
                            className="rounded-full px-8 py-3 text-sm font-medium transition-all duration-300 data-[state=active]:bg-yellow-500 data-[state=active]:text-black data-[state=active]:shadow-lg data-[state=active]:shadow-yellow-500/20 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-white/5"
                        >
                            <DollarSign className="w-4 h-4 mr-2" />
                            Gerenciar Solicitações
                        </TabsTrigger>
                        <TabsTrigger
                            value="overview"
                            className="rounded-full px-8 py-3 text-sm font-medium transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/20 data-[state=inactive]:text-gray-400 data-[state=inactive]:hover:text-white data-[state=inactive]:hover:bg-white/5"
                        >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Visão Geral
                        </TabsTrigger>
                    </TabsList>
                </div>

                <TabsContent value="requests" className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-500 outline-none mt-0">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-gray-900/40 p-4 rounded-lg border border-gray-800">
                            <div className="flex gap-2 w-full md:w-auto">
                                <div className="relative flex-1 md:w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <input
                                        type="text"
                                        placeholder="Buscar por nome..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full bg-gray-900 border border-gray-700 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 w-full md:w-auto justify-end">
                                <Button
                                    variant={filterStatus === 'pending' ? 'default' : 'outline'}
                                    onClick={() => setFilterStatus('pending')}
                                    className={filterStatus === 'pending' ? 'bg-orange-600 hover:bg-orange-700' : 'border-gray-700 hover:bg-gray-800 text-gray-400'}
                                >
                                    Em Revisão
                                </Button>
                                <Button
                                    variant={filterStatus === 'all' ? 'default' : 'outline'}
                                    onClick={() => setFilterStatus('all')}
                                    className={filterStatus === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'border-gray-700 hover:bg-gray-800 text-gray-400'}
                                >
                                    Todos
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleRefresh}
                                    className={`bg-blue-600 hover:bg-blue-700 text-white border-0 ${isRefreshing ? 'opacity-70' : ''}`}
                                    disabled={isRefreshing}
                                >
                                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                                    {isRefreshing ? 'Atualizando...' : 'Atualizar'}
                                </Button>
                                <div className="h-8 w-[1px] bg-gray-700 mx-2 hidden md:block"></div>
                                <Button
                                    variant="outline"
                                    onClick={handleExportCSV}
                                    className="w-full md:w-auto bg-green-900/20 hover:bg-green-900/40 text-green-400 border-green-900/50"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Exportar
                                </Button>

                                {selectedMonth !== 'all' && (
                                    <Button
                                        variant="destructive"
                                        onClick={handleDeleteAll}
                                        disabled={paymentsInMonth.length === 0}
                                        className="w-full md:w-auto bg-red-900/50 hover:bg-red-900 text-red-200 border border-red-900"
                                    >
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Excluir Mês
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="text-sm text-gray-400 px-2">
                            {filteredPayments.length} solicitação(ões) encontrada(s) em {availableMonths.find(m => m.value === selectedMonth)?.label}
                        </div>

                        {/* List */}
                        <div className="space-y-4">
                            {filteredPayments.length === 0 ? (
                                <div className="text-center py-12 bg-gray-900/30 rounded-lg border border-gray-800">
                                    <p className="text-gray-500">Nenhuma solicitação encontrada com os filtros atuais.</p>
                                </div>
                            ) : (
                                filteredPayments.map((request) => {
                                    // Lookup detailed user info
                                    const requestUser = allUsersMap ? allUsersMap[request.userId] : null;

                                    // Derivar avatar, codename e cargo
                                    const avatarUrl = requestUser?.avatar
                                        ? `https://cdn.discordapp.com/avatars/${request.userId}/${requestUser.avatar}.png`
                                        : null;

                                    const codename = requestUser ? getStaffDisplayName(requestUser) : (request.userName || '-');

                                    const role = requestUser?.staffDetails?.role ||
                                        requestUser?.accessLevel ||
                                        'User';

                                    const pixKey = requestUser?.staffDetails?.bankingData?.pixKey || 'Não informado';
                                    const pixType = requestUser?.staffDetails?.bankingData?.pixKeyType || '';

                                    const formattedRole = getReadableRole(role);

                                    return (
                                        <Card key={request.id} className="bg-gray-900/40 border-gray-800 hover:bg-gray-900/60 transition-colors">
                                            <CardContent className="p-6">
                                                <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-6">
                                                    {/* User Info with Avatar */}
                                                    <div className="flex items-start gap-4 min-w-[250px]">
                                                        {avatarUrl ? (
                                                            <img
                                                                src={avatarUrl}
                                                                alt={codename}
                                                                className="h-12 w-12 rounded-full object-cover border-2 border-gray-700"
                                                            />
                                                        ) : (
                                                            <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg border-2 border-gray-700">
                                                                {codename?.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}

                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="font-bold text-white text-lg">{codename}</h3>
                                                                <Badge variant="outline" className="text-[10px] h-5 px-1 bg-gray-800 border-gray-700 text-gray-400">
                                                                    {formattedRole}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-gray-400">ID: {request.userId}</p>
                                                        </div>
                                                    </div>

                                                    {/* Values */}
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 flex-1">
                                                        <div>
                                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Valor Padrão</p>
                                                            <p className="text-white font-bold">{formatCurrency(request.standardValue)}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Valor Extra</p>
                                                            <p className="text-gray-300">{formatCurrency(request.extraValue)}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Solicitado Em</p>
                                                            <p className="text-gray-300 text-sm">
                                                                {new Date(request.createdAt).toLocaleDateString()} <span className="text-xs text-gray-600">{new Date(request.createdAt).toLocaleTimeString().slice(0, 5)}</span>
                                                            </p>
                                                        </div>

                                                        {/* Pix Info with Copy */}
                                                        <div onClick={() => handleCopyPix(pixKey)} className="cursor-pointer group relative">
                                                            <p className="text-xs text-blue-400 uppercase font-bold mb-1 group-hover:text-blue-300 transition-colors">
                                                                PIX ({pixType.toUpperCase() || 'CHAVE'})
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-white font-mono text-sm truncate max-w-[120px] group-hover:text-blue-100 transition-colors">
                                                                    {pixKey}
                                                                </p>
                                                                {pixKey !== 'Não informado' && (
                                                                    <Badge variant="secondary" className="text-[10px] h-4 px-1 bg-gray-800 text-gray-400 group-hover:bg-blue-900 group-hover:text-blue-200">
                                                                        COPIAR
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Last Request Info */}
                                                        {(() => {
                                                            const lastRequest = payments
                                                                .filter(p => p.userId === request.userId && p.id !== request.id && new Date(p.createdAt) < new Date(request.createdAt))
                                                                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

                                                            return (
                                                                <div>
                                                                    <p className="text-xs text-purple-400 uppercase font-bold mb-1">Última Solicitação</p>
                                                                    <p className="text-gray-300 text-xs text-nowrap">
                                                                        {lastRequest ? new Date(lastRequest.createdAt).toLocaleDateString() : 'Nenhuma'}
                                                                    </p>
                                                                    <p className="text-white font-bold text-sm">
                                                                        {lastRequest ? formatCurrency(lastRequest.totalValue) : '-'}
                                                                    </p>
                                                                </div>
                                                            );
                                                        })()}

                                                        <div className="flex items-center justify-end md:justify-start">
                                                            <StatusBadge status={request.status} />
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex flex-col items-end gap-2 min-w-[150px]">
                                                        <div className="mb-2 text-right">
                                                            <p className="text-xs text-yellow-500 font-bold uppercase mb-1">Total Solicitado</p>
                                                            <p className="text-2xl font-bold text-white">{formatCurrency(request.totalValue)}</p>
                                                        </div>

                                                        {request.status === 'pending' && (
                                                            <div className="flex gap-2 w-full">
                                                                <Button
                                                                    onClick={() => handleApprove(request.id)}
                                                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                                                                    size="sm"
                                                                >
                                                                    Aprovar
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleReject(request.id)}
                                                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                                                    size="sm"
                                                                >
                                                                    Rejeitar
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {request.observation && (
                                                    <div className="mt-4 pt-4 border-t border-gray-800">
                                                        <p className="text-sm text-gray-400"><span className="font-bold text-gray-500">Observação:</span> {request.observation}</p>
                                                    </div>
                                                )}

                                                {request.status === 'rejected' && request.rejectionReason && (
                                                    <div className="mt-4 pt-4 border-t border-gray-800 bg-red-900/10 -mx-6 -mb-6 p-6 rounded-b-xl">
                                                        <p className="text-sm text-red-400">
                                                            <span className="font-bold text-red-500">Motivo da Rejeição:</span> {request.rejectionReason}
                                                        </p>
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="overview" className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500 outline-none mt-0">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="bg-orange-600 border-transparent">
                            <CardContent className="p-6 flex justify-between items-center">
                                <div>
                                    <p className="text-xs font-bold text-orange-200 uppercase mb-1">Em Revisão</p>
                                    <h3 className="text-3xl font-bold text-white">{monthStats.pendingCount}</h3>
                                </div>
                                <Clock className="h-8 w-8 text-white/50" />
                            </CardContent>
                        </Card>

                        <Card className="bg-green-600 border-transparent">
                            <CardContent className="p-6 flex justify-between items-center">
                                <div>
                                    <p className="text-xs font-bold text-green-200 uppercase mb-1">Aprovadas</p>
                                    <h3 className="text-3xl font-bold text-white">{monthStats.approvedCount}</h3>
                                </div>
                                <CheckCircle2 className="h-8 w-8 text-white/50" />
                            </CardContent>
                        </Card>

                        <Card className="bg-red-600 border-transparent">
                            <CardContent className="p-6 flex justify-between items-center">
                                <div>
                                    <p className="text-xs font-bold text-red-200 uppercase mb-1">Rejeitadas</p>
                                    <h3 className="text-3xl font-bold text-white">{monthStats.rejectedCount}</h3>
                                </div>
                                <XCircle className="h-8 w-8 text-white/50" />
                            </CardContent>
                        </Card>

                        <Card className="bg-blue-600 border-transparent">
                            <CardContent className="p-6 flex justify-between items-center">
                                <div>
                                    <p className="text-xs font-bold text-blue-200 uppercase mb-1">Valor Aguardando</p>
                                    <h3 className="text-2xl font-bold text-white">{formatCurrency(monthStats.awaitingPaymentValue)}</h3>
                                </div>
                                <DollarSign className="h-8 w-8 text-white/50" />
                            </CardContent>
                        </Card>

                        <Card className="bg-purple-600 border-transparent col-span-1 md:col-span-2 lg:col-span-4">
                            <CardContent className="p-6 flex justify-between items-center">
                                <div>
                                    <p className="text-xs font-bold text-purple-200 uppercase mb-1">Aprovado este Mês</p>
                                    <h3 className="text-2xl font-bold text-white">{formatCurrency(monthStats.approvedValue)}</h3>
                                </div>
                                <Calendar className="h-8 w-8 text-white/50" />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Trend Chart */}
                    <Card className="bg-gray-900/30 border-gray-800">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart3 className="h-5 w-5 text-blue-500" />
                                <h3 className="text-lg font-bold text-white">Tendência Financeira (últimos 6 meses)</h3>
                            </div>
                            <div className="h-[350px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={trendData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.5} />
                                        <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `R$ ${value}`} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '8px' }}
                                            itemStyle={{ color: '#E5E7EB' }}
                                            formatter={(value) => formatCurrency(value)}
                                        />
                                        <Legend />
                                        <Bar dataKey="Solicitado" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={50} />
                                        <Bar dataKey="Aprovado" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={50} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Rejection Modal */}
            {rejectionModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-gray-900 border border-red-500/30 rounded-2xl w-full max-w-md p-6 shadow-2xl">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <XCircle className="text-red-500" />
                            Rejeitar Solicitação
                        </h3>
                        <p className="text-gray-400 mb-4">
                            Informe o motivo da rejeição. Isso será visível para o prestador.
                        </p>
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-white focus:ring-2 focus:ring-red-500 outline-none min-h-[100px] mb-6"
                            placeholder="Ex: Dados bancários incorretos..."
                        />
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => setRejectionModalOpen(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancelar
                            </Button>
                            <Button
                                onClick={confirmRejection}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                Confirmar Rejeição
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default PaymentManagement;
